import { z } from "zod";
import { SUPPORTED_LANGS } from "../i18n/langs";

export const meUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  avatar: z.string().nullable(),
  /** Needed so the sign-up wizard can tell whether the (now required) phone step is still outstanding. */
  phone: z.string().nullable().default(null),
  vibes: z.array(z.string()).default([]),
});

export type MeUser = z.infer<typeof meUserSchema>;

export const meResponseSchema = z.object({
  user: meUserSchema,
});

export type MeResponse = z.infer<typeof meResponseSchema>;

export const profileUpdateRequestSchema = z.object({
  firstName: z.string().trim().min(1),
  /*
   * Required, and that is the point of the one Full name field: it is split on
   * the first space, so an empty last name means the traveller typed a single
   * word. Allowing it let "rddd" save as a whole identity, and the name on a
   * booking is what the provider calls out at the meeting point (owner,
   * 2026-09-10: "user can't save only 1 name, must be full name").
   *
   * The profile screen is the only consumer; the sign-up wizard patches
   * PATCH /me field by field and is unaffected.
   */
  lastName: z.string().trim().min(1),
  email: z.string().email(),
  // `phone` is the legacy single-string shape the full profile-edit form
  // (profile-screen.tsx) still submits. `phoneCode`/`phoneNumber` are the two
  // parts the passwordless sign-up wizard's phone step saves incrementally —
  // `PATCH /me` (apps/api/src/features/me/routes/profile.ts) accepts both
  // shapes independently, so both stay on the type rather than forcing every
  // caller onto one.
  phone: z.string().regex(/^\+\d{6,15}$/),
  phoneCode: z.string().regex(/^\+\d{1,4}$/).optional(),
  phoneNumber: z.string().min(6).optional(),
  /**
   * The picker's own id (`Country.id`, an integer), and optional.
   *
   * It was required, and most accounts have no country on file — so the
   * resolver failed on a field with no error slot anywhere on the screen and
   * Save did nothing at all, silently, for everyone in that state. The route
   * takes it as an optional *string* (a uuid column), so the form converts on
   * the way out; see `use-update-profile-mutation`.
   */
  countryId: z.number().optional(),
  cityId: z.string().optional(),
  // Wider than the API's PATCH /me literal en/fr/ar union on purpose:
  // pref-language-screen.tsx (pre-existing, out of Task 13's scope) offers
  // all 7 content languages and sends whichever one the user picks. That
  // screen already relied on this being the full SUPPORTED_LANGS set before
  // this task; narrowing it here would break its typecheck without fixing
  // the screen itself.
  languageCode: z.enum(SUPPORTED_LANGS),
  currencyCode: z.string(),
  paymentMethod: z.enum(["cash", "card", "bank_transfer"]),
  pushNotifications: z.boolean(),
  vibes: z.array(z.string()).optional(),
});
export type ProfileUpdateRequest = z.infer<typeof profileUpdateRequestSchema>;
